<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import { userMethods, userComputed } from "@/state/helpers";
import { roleMethods, roleComputed } from "@/state/helpers";
import Swal from "sweetalert2";

export default {
  components: { Layout, PageHeader },
  data() {
    return {
      tableData: [],
      hover: true,
      title: "All Users",
      totalRows: 1,
      currentPage: 1,
      perPage: 10,
      pageOptions: [10, 25, 50, 100],
      sortByOptions: [
        {
          value: "created_at",
          text: "Date",
        },
        {
          value: "id",
          text: "ID",
        },
        {
          value: "first_name",
          text: "Name",
        },
      ],
      sortOrderOptions: [
        {
          value: "desc",
          text: "Desc",
        },
        {
          value: "asc",
          text: "Asc",
        },
      ],
      filter: {
        role: "",
        q: "",
        sortBy: "created_at",
        sortOrder: "desc",
      },
    };
  },
  computed: {
    ...userComputed,
    ...roleComputed,
    filterApplied() {
      return (
        this.filter.role ||
        this.filter.q ||
        this.filter.sortBy != "created_at" ||
        this.filter.sortOrder != "desc"
      );
    },
  },
  methods: {
    ...userMethods,
    ...roleMethods,
    getUsers(page) {
      if (isNaN(page)) {
        page = 1;
      }
      this.currentPage = page;
      this.loadUsers({
        page: this.currentPage,
        perPage: this.perPage,
        role: this.filter.role,
        q: this.filter.q,
        sortBy: this.filter.sortBy,
        sortOrder: this.filter.sortOrder,
      }).then(
        (products) => {
          this.totalRows = products.total;
        },
        function (error) {
          Swal.fire({
            icon: "error",
            title: "An error occured",
            text: JSON.stringify(error),
          });
        }
      );
    },
    onPageSizeChange() {
      this.getUsers();
    },
    resetFilters() {
      this.filter.role = "";
      this.filter.q = "";
      this.filter.sortBy = "created_at";
      this.filter.sortOrder = "desc";
      this.getUsers();
    },
  },
  mounted() {
    this.getUsers();
    this.loadRoles();
  },
};
</script>
    
<template>
  <Layout>
    <PageHeader :title="title" />
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <b-button :to="{ name: 'user-create' }">Create User</b-button>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="row mt-4 mb-4">
              <div class="col-2">
                <label>Search</label>
                <b-form-input
                  v-model="filter.q"
                  type="search"
                  placeholder="User ID / Name / Email"
                  class="form-control form-control"
                ></b-form-input>
              </div>
              <div class="col-2">
                <label>User Role</label>
                <b-form-select
                  class="form-select form-select-sm"
                  v-model="filter.role"
                  size="sm"
                  :options="roles"
                  value-field="id"
                  text-field="name"
                >
                  <option slot="first" :value="''">-- All --</option>
                </b-form-select>
              </div>
              <div class="col-2">
                <div>
                  <label for="orders-sort-column">Sort By</label>
                  <b-form-select
                    size="sm"
                    class="form-select form-select-sm"
                    id="orders-sort-column"
                    v-model="filter.sortBy"
                    :options="sortByOptions"
                  ></b-form-select>
                </div>
              </div>
              <div class="col-2">
                <div>
                  <label for="orders-sort-order">Sort Order</label>
                  <b-form-select
                    size="sm"
                    id="orders-sort-order"
                    class="form-select form-select-sm"
                    v-model="filter.sortOrder"
                    :options="sortOrderOptions"
                  ></b-form-select>
                </div>
              </div>
              <div class="col-2 offset-2 text-end">
                <b-button
                  v-if="filterApplied"
                  type="button"
                  class="mt-4 me-2"
                  @click="resetFilters"
                >
                  <span>Reset</span>
                </b-button>
                <b-button type="button" class="mt-4" @click="getUsers">
                  <span>Search</span>
                </b-button>
              </div>
            </div>
            <!-- Table -->
            <div class="table-responsive mb-0">
              <table class="table table-bordered">
                <thead>
                  <tr>
                    <th scope="col">ID</th>
                    <th scope="col">Name</th>
                    <th scope="col">Email</th>
                    <th scope="col">Role</th>
                    <th scope="col">Creation Date</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="user in users" :key="user.id">
                    <td>#{{ user.id }}</td>
                    <td>
                      <b-link
                        class="d-inline-block p-1 px-2 border border-rounded"
                        :to="{ name: 'user-edit', params: { id: user.id } }"
                      >
                        <span v-if="user.first_name && user.last_name">
                          {{ user.first_name + " " + user.last_name }}
                        </span>
                        <span v-else-if="user.first_name">
                          {{ user.first_name }}
                        </span>
                        <span v-else> Not Defined </span>
                      </b-link>
                    </td>
                    <td>{{ user.email }}</td>
                    <td>
                      <span
                        v-for="role in user.roles"
                        :key="role.id"
                        class="d-inline-block border rounded px-2 py-1"
                        :class="{
                          'bg-success text-white': role.id == 1, //Admin
                          'bg-info text-white': role.id == 2, //Staff
                          'bg-secondary text-white': role.id == 3, //Customer
                        }"
                      >
                        {{ role.name }}
                      </span>
                    </td>
                    <td>{{ moment(user.created_at).format("YYYY-MM-DD") }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="row">
              <div class="col">
                <label class="d-inline-flex align-items-center">
                  <span class="p-2">Show</span>
                  <b-form-select
                    class="form-select form-select-sm"
                    v-model="perPage"
                    size="sm"
                    :options="pageOptions"
                    @change="onPageSizeChange"
                  ></b-form-select>
                  <span class="p-2">entries</span>
                </label>
              </div>
              <div class="col">
                <div
                  class="dataTables_paginate paging_simple_numbers float-end"
                >
                  <ul class="pagination pagination-rounded mb-0">
                    <!-- pagination -->
                    <b-pagination
                      v-model="currentPage"
                      :total-rows="totalRows"
                      :per-page="perPage"
                      @change="getUsers"
                    ></b-pagination>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>